"use client";

import * as React from "react";
import {
	DropdownMenu as Root,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { usePathname } from "next/navigation";
import {
	isNavHrefActive,
	NAV_ICON_CLASS,
	navLabelClass,
} from "../nav-active";

type MenuItem = {
	href: string;
	label: string;
	icon?: React.ReactNode;
	className?: string;
};

type SmartDropdownProps = {
	label: string;
	icon?: React.ReactNode;
	items: MenuItem[];
	labelClass?: string;
	triggerClass?: string;
	contentClass?: string;
	itemTextClass?: string;
	showChevron?: boolean;
	/** Sottolineatura sulla label quando una voce del menu è attiva (tablet/mobile). */
	underlineActive?: boolean;
};

export function SmartDropdown({
	label,
	icon,
	items,
	labelClass = "mt-2 text-base md:text-lg lg:text-xl",
	triggerClass,
	contentClass,
	itemTextClass = "text-base md:text-lg lg:text-xl",
	showChevron = true,
	underlineActive = false,
}: SmartDropdownProps) {
	const pathname = usePathname();
	const isDropdownActive = items.some((it) =>
		isNavHrefActive(pathname, it.href),
	);

	return (
		<Root>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					aria-haspopup="menu"
					className={cn(
						"p-0 cursor-pointer group flex flex-col items-center justify-center font-semibold bg-(--bg-secondary) transition-colors outline-none",
						triggerClass,
					)}
				>
					<div className="flex flex-col items-center">
						<div className={cn("flex flex-row items-center gap-1", NAV_ICON_CLASS)}>
							{icon}
							{showChevron && (
								<ChevronDown
									aria-hidden
									className="h-4 w-4 transition-transform duration-300 group-data-[state=open]:rotate-180"
								/>
							)}
						</div>
						<span
							className={cn(
								labelClass,
								navLabelClass(isDropdownActive, underlineActive),
							)}
						>
							{label}
						</span>
					</div>
				</button>
			</DropdownMenuTrigger>

			<DropdownMenuContent
				className={cn(
					"min-w-[--radix-dropdown-menu-trigger-width] bg-card text-card-foreground",
					contentClass,
				)}
			>
				{items.map((it) => {
					const itemActive = isNavHrefActive(pathname, it.href);
					return (
						<DropdownMenuItem
							key={it.href}
							asChild
							className={cn(itemTextClass, it.className)}
						>
							<Link
								href={it.href}
								className={cn(
									"group/item font-semibold flex items-center gap-2 px-4 py-2 transition-colors cursor-pointer",
									"hover:bg-[#0a9cd8]/15",
									navLabelClass(itemActive, underlineActive),
								)}
								aria-current={itemActive ? "page" : undefined}
							>
								{it.icon && (
									<span
										className={cn(
											NAV_ICON_CLASS,
											"*:h-5 *:w-5 md:*:h-6 md:*:w-6",
										)}
									>
										{it.icon}
									</span>
								)}
								<span>{it.label}</span>
							</Link>
						</DropdownMenuItem>
					);
				})}
			</DropdownMenuContent>
		</Root>
	);
}
